/**
 * The route gate for /shoutout-wall (lib/wallPause.ts). While award winners
 * are hidden, the wall is open to everyone for shout-outs and a Director sees
 * a banner saying the awards are hidden from everyone else. If the whole wall
 * is ever hidden again (SHOUTOUT_WALL_HIDDEN), the note below takes over.
 * Everyone but a Director gets a short "getting an update" note and never
 * mounts the page, so no wall card or shout-out is read into their browser. A
 * Director sees the page with a plain banner saying who can see it, so Kim is
 * not left thinking families are looking at it.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserDoc } from '../lib/users';
import StudioLoading from './StudioLoading';
import {
  AWARDS_HIDDEN_UNTIL_POSTED,
  canSeeShoutoutWall,
  SHOUTOUT_WALL_HIDDEN,
  WALL_HIDDEN_DIRECTOR_NOTE,
  WALL_HIDDEN_NOTICE_BODY,
  WALL_HIDDEN_NOTICE_TITLE,
} from '../lib/wallPause';

export default function ShoutoutWallGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { userDoc, loading } = useUserDoc();
  if (loading) return <StudioLoading />;
  const role = userDoc?.role ?? null;
  if (!SHOUTOUT_WALL_HIDDEN && !(AWARDS_HIDDEN_UNTIL_POSTED && role === 'director')) return <>{children}</>;
  if (!canSeeShoutoutWall(role)) {
    return (
      <div
        data-wall-hidden-notice
        style={{
          maxWidth: 560,
          margin: '0 auto',
          padding: 'var(--space-5) var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <h1 style={{ margin: 0, fontFamily: 'var(--font-poster)', fontWeight: 400, textTransform: 'uppercase', fontSize: 'var(--text-poster-m)', color: 'var(--ink)' }}>
          {WALL_HIDDEN_NOTICE_TITLE}
        </h1>
        <p style={{ margin: 0, fontSize: 16, lineHeight: 1.45, color: 'var(--ink-soft)' }}>{WALL_HIDDEN_NOTICE_BODY}</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            alignSelf: 'flex-start',
            minHeight: 'var(--tap-target)',
            padding: '0 var(--space-4)',
            borderRadius: 'var(--radius-pill)',
            border: '1px solid var(--line)',
            background: 'var(--paper)',
            color: 'var(--ink)',
            fontWeight: 800,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }
  return (
    <>
      <div
        role="status"
        data-wall-hidden-banner
        style={{
          maxWidth: 1100,
          margin: 'var(--space-3) auto 0',
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-md)',
          border: '2px solid var(--amber-border)',
          background: 'var(--amber-bg)',
          color: 'var(--amber-text)',
          fontWeight: 800,
          fontSize: 15,
          lineHeight: 1.4,
        }}
      >
        {WALL_HIDDEN_DIRECTOR_NOTE}
      </div>
      {children}
    </>
  );
}
